import i18n from 'i18next';
import { Children, cloneElement, isValidElement, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
import { assinarDiscreto, discretoAtivo, iniciais, nomesOcultos, OCULTO, prefsDiscreto } from '../nucleo/discreto';

/** Estado do modo discreto para componentes (renderiza de novo na troca, nesta aba e nas outras). */
export function useDiscreto() {
  const ativo = useSyncExternalStore(assinarDiscreto, discretoAtivo, discretoAtivo);
  const prefs = useSyncExternalStore(assinarDiscreto, prefsDiscreto, prefsDiscreto);
  return { ativo, prefs, nomes: ativo && prefs.nomes };
}

/** Trecho censurado: uma ou mais sequências de "•" ligadas por separador ("•••.", "(••) •••••"). */
const MARCA = /•{2,}/;
const MARCAS = /•+(?:[\s.\-/()]*•+)*/g;

/**
 * Texto com valor oculto para o leitor de tela: a parte visível ("R$ •••••") fica `aria-hidden` e o leitor ouve
 * "R$ valor oculto" no idioma do app. Texto sem marca volta como veio.
 */
/** O que o leitor de tela deve ouvir no lugar de um texto com marca de oculto ("R$ •••••" vira "R$ valor oculto"). */
export const temMarcaOculto = (texto: string) => MARCA.test(texto);
export const textoFalado = (texto: string) => texto.replace(MARCAS, ` ${i18n.t('discreto.valorOculto')} `).replace(/\s+/g, ' ').trim();

function anunciarTexto(texto: string, chave?: number): ReactNode {
  if (!MARCA.test(texto)) return texto;
  const falado = textoFalado(texto);
  return (
    // Sem `title` (r10 visual B11): o tooltip com os pontos virava a descrição acessível e o leitor soletrava "ponto ponto".
    <span key={chave} data-oculto="" className="relative">
      <span aria-hidden="true">{texto}</span>
      <span className="sr-only">{falado}</span>
    </span>
  );
}

/**
 * Percorre o conteúdo (texto e elementos HTML, não componentes) e troca cada texto com valor oculto pela versão que o leitor
 * de tela anuncia como "valor oculto". Usado nas células de tabela, cartões de indicador e linhas de ficha do núcleo.
 */
export function anunciarOcultos(no: ReactNode): ReactNode {
  if (!discretoAtivo()) return no;
  if (typeof no === 'string') return anunciarTexto(no);
  if (Array.isArray(no)) return Children.map(no, (f, i) => (typeof f === 'string' ? anunciarTexto(f, i) : anunciarOcultos(f)));
  if (isValidElement<{ children?: ReactNode }>(no) && typeof no.type === 'string' && no.props.children !== undefined) {
    return cloneElement(no as ReactElement<{ children?: ReactNode }>, undefined, anunciarOcultos(no.props.children));
  }
  return no;
}

/**
 * Texto livre marcado como sensível (observação, detalhe que traz valor): com o modo discreto ligado vira "•••••" e o leitor
 * de tela anuncia "valor oculto". `tipo="nome"` segue a preferência de nomes (iniciais) em vez do modo inteiro.
 */
export function Sensivel({ children, tipo = 'texto', className }: { children: string | null | undefined; tipo?: 'texto' | 'nome'; className?: string }) {
  const { ativo, nomes } = useDiscreto();
  const texto = children ?? '';
  if (!texto) return null;
  if (tipo === 'nome') {
    if (!nomes) return <span className={className}>{texto}</span>;
    return (
      <span className={`relative ${className ?? ''}`} data-oculto="">
        <span aria-hidden="true">{iniciais(texto)}</span>
        <span className="sr-only">{`${iniciais(texto)} ${i18n.t('discreto.valorOculto')}`}</span>
      </span>
    );
  }
  if (!ativo) return <span className={className}>{texto}</span>;
  return (
    <span className={`relative ${className ?? ''}`} data-oculto="">
      <span aria-hidden="true">{OCULTO}</span>
      <span className="sr-only">{i18n.t('discreto.valorOculto')}</span>
    </span>
  );
}

/** Texto (não ReactNode) para `title`, `aria-label` e afins: censurado quando o modo discreto está ligado. */
export function textoSensivel(texto: string | null | undefined, tipo: 'texto' | 'nome' = 'texto') {
  if (!texto) return '';
  if (tipo === 'nome') return nomesOcultos() ? iniciais(texto) : texto;
  return discretoAtivo() ? OCULTO : texto;
}

export { OCULTO };
